import { type ComponentProps, useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import { cn } from '@/lib/utils'

export const fieldClasses = cn(
  'w-full min-w-0 rounded-md border border-input bg-background px-3 py-1 text-base shadow-xs md:text-sm',
  'placeholder:text-muted-foreground transition-[color,box-shadow] duration-150',
  'focus-visible:border-ring focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
  'disabled:cursor-not-allowed disabled:opacity-50',
  'aria-invalid:border-destructive aria-invalid:ring-destructive/20',
)

export function Input({ className, type = 'text', ...props }: ComponentProps<'input'>) {
  return <input data-slot="input" type={type} className={cn(fieldClasses, 'h-9 max-md:h-11', className)} {...props} />
}

/** A text box that grows with its text (its `rows` are the minimum) and is never resized by hand. */
export function Textarea({ className, onInput, ref, ...props }: ComponentProps<'textarea'>) {
  const box = useRef<HTMLTextAreaElement | null>(null)
  const fit = useCallback(() => {
    const element = box.current
    if (!element) return
    element.style.height = 'auto'
    element.style.height = `${element.scrollHeight + element.offsetHeight - element.clientHeight}px`
  }, [])
  // Text set from outside (a loaded record, a reset) resizes too, not only typing.
  useLayoutEffect(fit, [fit, props.value, props.defaultValue])
  // A narrower box wraps onto more lines (phone rotated, window resized): fit again.
  useEffect(() => {
    window.addEventListener('resize', fit)
    return () => window.removeEventListener('resize', fit)
  }, [fit])
  const setRef = useCallback(
    (element: HTMLTextAreaElement | null) => {
      box.current = element
      if (typeof ref === 'function') ref(element)
      else if (ref) ref.current = element
    },
    [ref],
  )
  return (
    <textarea
      data-slot="textarea"
      ref={setRef}
      onInput={(event) => {
        fit()
        onInput?.(event)
      }}
      className={cn(fieldClasses, 'min-h-20 resize-none overflow-hidden py-2', className)}
      {...props}
    />
  )
}
